<template>
  <div class="col">
    <div class="row items-center">
      <!-- step -->
      <div class="col-auto text-center">
        <Component
          :is="icon"
          class="icon block mx-auto mb-1 image-contain"
          width="70"
          height="50"
        />
        <a
          :href="`#${step.categoryId}`"
          class="nav-link btn btn-outline-primary py-1 stretched-link font-medium rounded-pill border-primary-100"
          @click="scrollToAnchor"
        >
          {{ currentIndex + 1 }}. {{ name }}
        </a>
      </div>
      <!-- arrow -->
      <div
        v-if="!isLast"
        class="col text-center"
      >
        <i class="mi-arrow-r text-primary opacity-30 icon-28" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },

  isLast: {
    type: Boolean,
    default: false,
  },

  currentIndex: {
    type: Number,
    default: 1,
  },
})

const icon = ref('')
const name = ref()

watch(
  () => props.step.categoryId,
  () => {
    iconParams()
  }
)
const scrollToAnchor = (event) => {
  event.preventDefault()

  const anchorLinks = document.querySelector('#js-anchor-links')
  const navbar = document.querySelector('#js-navbar')
  const totalOffset = (anchorLinks ? anchorLinks.offsetHeight : 0) + (navbar ? navbar.offsetHeight : 0)

  const href = event.target.getAttribute('href')
  const targetElement = document.querySelector(`#${href.replace('#', '')}`)
  const targetOffsetTop = targetElement.getBoundingClientRect().top + document.documentElement.scrollTop - totalOffset

  window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' })
}

const onScroll = () => {
  const scrollPoint = window.scrollY
  const scrollSections = document.querySelectorAll('[all-categories]')
  let currentId = 0
  if (scrollSections.length > 1) {
    for (const [index, value] of scrollSections.entries()) {
      if (scrollPoint > value.offsetTop) {
        currentId = index
      }

      if (document.querySelector(`[href="#${value.id}"]`) !== null) {
        document.querySelector(`[href="#${value.id}"]`).classList.remove('current-section')
      }
    }
    if (scrollSections[currentId].id === 'musli_base_extended') {
      document.querySelector(`[href="#${scrollSections[currentId - 1].id}"]`).classList.add('current-section')
    }
    if (scrollSections[currentId].id === 'crunchy_fruits') {
      document.querySelector(`[href="#${scrollSections[currentId + 1].id}"]`).classList.add('current-section')
    }
    if (document.querySelector(`[href="#${scrollSections[currentId].id}"]`) !== null) {
      document.querySelector(`[href="#${scrollSections[currentId].id}"]`).classList.add('current-section')
    }
  }
}

const iconParams = () => {
  switch (props.step.categoryId) {
    case 'musli_base': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/BaseMixesSvg.vue'))
      name.value = t('musli_base')
      break
    }
    case 'porridge_base': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/PorridgeSvg.vue'))
      name.value = t('porridge_base')
      break
    }
    case 'dry_fruits': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/FruitSvg.vue'))
      name.value = t('dry_fruits')
      break
    }
    case 'crunchy_fruits': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/FruitSvg.vue'))
      name.value = t('crunchy_fruits')
      break
    }
    case 'nuts': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/NutsSvg.vue'))
      name.value = t('nuts')
      break
    }
    case 'mixit_bars': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/BarsSvg.vue'))
      name.value = t('mixit_bars')
      break
    }
    case 'fruit_bags': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/BagsSvg.vue'))
      name.value = t('fruit_bags')
      break
    }
    case 'on_top': {
      icon.value = defineAsyncComponent(() => import('@/components/svg/mixes/ChocolateSvg.vue'))
      name.value = t('on_top_category')
      break
    }
    default: {
      icon.value = defineAsyncComponent(() => import('@/components/svg/MixitSvg.vue'))
    }
    // do nothing
  }
}
iconParams()

onMounted(() => {
  window.addEventListener('scroll', onScroll)
})
</script>

<style lang="scss">
.current-section {
  color: $primary-color-400;
  background-color: $primary-color-800;
  border-color: $primary-color-800;
}
</style>

<i18n>
  {
    "cs": {
    "crunchy_fruits": "Křupavé ovoce",
    "dry_fruits": "Sušené ovoce",
    "fruit_bags": "Pytlíky do kapsy",
    "mixit_bars": "Mixitky",
    "musli_base": "Müsli základ",
    "nuts": "Ořechy",
    "on_top_category": "Čoko & spol.",
    "porridge_base": "Základ kaše"
},
    "sk": {
    "crunchy_fruits": "Chrumkavé ovocie",
    "dry_fruits": "Sušené ovocie",
    "fruit_bags": "Vrecúška do vrecka",
    "mixit_bars": "Mixitky",
    "musli_base": "Müsli základ",
    "nuts": "Orechy",
    "on_top_category": "Čoko & spol.",
    "porridge_base": "Základ kaše"
},
    "pl": {
    "crunchy_fruits": "Chrupiące owoce",
    "dry_fruits": "Suszone owoce",
    "fruit_bags": "Saszetki do kieszeni",
    "mixit_bars": "Mixitki",
    "musli_base": "Podstawa müsli",
    "nuts": "Orzechy",
    "on_top_category": "Czekolada & dodatki",
    "porridge_base": "Podstawa kaszy"
},
    "hu": {
    "crunchy_fruits": "Ropogós gyümölcsök",
    "dry_fruits": "Szárított gyümölcs",
    "fruit_bags": "Zsebbe való tasakok",
    "mixit_bars": "Mixitkák",
    "musli_base": "Müzli alap",
    "nuts": "Diófélék",
    "on_top_category": "Csoki & társai",
    "porridge_base": "Kása alap"
},
    "de": {
    "crunchy_fruits": "Knusperfrüchte",
    "dry_fruits": "Trockenfrüchte",
    "fruit_bags": "Snacktaschen",
    "mixit_bars": "Mixitriegel",
    "musli_base": "Müslibasis",
    "nuts": "Nüsse",
    "on_top_category": "Schokolade & Co.",
    "porridge_base": "Breibasis"
},
    "at": {
    "crunchy_fruits": "Knusperfrüchte",
    "dry_fruits": "Trockenfrüchte",
    "fruit_bags": "Snacktaschen",
    "mixit_bars": "Mixitriegel",
    "musli_base": "Müslibasis",
    "nuts": "Nüsse",
    "on_top_category": "Schokolade & Co.",
    "porridge_base": "Breibasis"
},
    "ro": {
    "crunchy_fruits": "Fructe crocante",
    "dry_fruits": "Fructe uscate",
    "fruit_bags": "Punguțe",
    "mixit_bars": "Mixitka",
    "musli_base": "Musli bază",
    "nuts": "Nuci",
    "on_top_category": "Cioco & co.",
    "porridge_base": "Terci bază"
},
    "fr": {
    "crunchy_fruits": "Fruits croustillants",
    "dry_fruits": "Fruits secs",
    "fruit_bags": "Format poche",
    "mixit_bars": "Mixitka",
    "musli_base": "Base de muesli",
    "nuts": "Fruits à coque",
    "on_top_category": "Choco et compagnie",
    "porridge_base": "Base du porridge"
}
  }
</i18n>
